import {Link, useLoaderData, useOutletContext, useRouteLoaderData} from '@remix-run/react'
import type {SyntheticEvent} from 'react'
import React, {Fragment, useCallback} from 'react'
import clsx from 'clsx'
import {type MetaFunction, json, type LoaderFunction} from '@remix-run/node'
import {slugToText, textToSlug} from '@roundforest/text-transforms-commons'
import {getTranslation} from '~/localization/translation'
import {IconAmazonLogoSvg} from '~/icons/product-page/icon-amazon-logo-svg'
import {KeyFeaturesIcon} from '~/icons/product-page/key-features-icon'
import {ChosenByCheckIcon} from '~/icons/product-page/chosen-by-check-icon'
import {BackArrowIcon} from '~/icons/product-features-page/back-arrow-icon'

import type {
  ProductFeaturesPageLoaderResponse,
  RootAndListContext,
  RootLoaderResponse,
  UserValueProps,
  UserValueStarsProps,
} from '~/types'

import {
  fetchProductFeaturesPageData,
  fetchProductFeaturesPageFakeData,
} from '~/api/product-features-page-data-fetcher.server'
import {CheckBWIcon} from '~/icons/product-features-page/check-bw-icon'
import {ValueForMoneyIcon} from '~/icons/product-features-page/value-for-money-icon'
import {QualityIcon} from '~/icons/product-features-page/quality-icon'
import {EasyToUseIcon} from '~/icons/product-features-page/easy-to-use-icon'
import {useReportWidgetClick, useReportWidgetSeen, useSendPartnerProductClickoutCustom} from '~/hooks/analytic-hooks'

import {setEnvironmentWith} from '~/utils/async-storage.server'

import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {getAsinBasedSemiRandomNumber, getSchemaPriceValidUntil} from '~/utils/commons'
import {getDomainConfig} from '~/domains'
import {DiscountBadge} from '../$slug._index/badges/discount-badge'
import ProductCardBadge from '../$slug._index/badges/card-placement-badge'

export const meta: MetaFunction<typeof loader> = ({data}: {data: ProductFeaturesPageLoaderResponse}) => {
  const locale = data?.locale?.toLowerCase() ?? 'us'
  const listSlug = data?.listSlug
  const domain = data?.domain
  const {camelCaseUrl, description} = getDomainConfig(domain, locale)
  const productSlug = data?.productPageData?.productTitleSlug
  const productImage = data?.productPageData?.product?.image
  const productName = slugToText(productSlug?.slice(0, productSlug?.lastIndexOf('-')))
  const metaTitle = `${productName} Review | ${camelCaseUrl}`
  const metaDescription = description(productName, '')
  return [
    {
      title: metaTitle,
    },
    {
      name: 'description',
      content: metaDescription,
    },
    {
      name: 'viewport',
      content: 'width=device-width,initial-scale=1',
    },
    {
      name: 'charset',
      content: 'utf-8',
    },
    {
      property: 'og:url',
      content: `https://${domain}/${listSlug}/${productSlug}`,
    },
    {
      property: 'og:locale',
      content: data?.locale?.toUpperCase(),
    },
    {
      property: 'og:image:width',
      content: '1024',
    },
    {
      property: 'og:image:height',
      content: '1024',
    },
    {
      property: 'og:image',
      content: productImage,
    },
    {
      property: 'og:image:alt',
      content: 'Product Image',
    },
    {
      property: 'og:title',
      content: metaTitle,
    },
    {
      property: 'og:description',
      content: metaDescription,
    },
    {
      property: 'og:type',
      content: 'website',
    },
  ]
}

export const loader: LoaderFunction = async ({context, params}) => {
  setEnvironmentWith(context)
  const {locale, domain, language} = context

  const slug = textToSlug(params?.slug || '')?.replace('best-', '')
  const productSlug = params?.productSlug || ''

  const fetchResponse =
    process.env.NODE_ENV !== 'production'
      ? await fetchProductFeaturesPageFakeData()
      : await fetchProductFeaturesPageData({
          listSlug: slug,
          productSlug,
          imageProxyCredentials: {
            host: process.env.IMAGE_PROXY_HOST!,
            key: process.env.IMAGE_PROXY_KEY!,
          },
          productPageVariation: 'BASELINE',
        })

  if (fetchResponse.type === 'success') {
    return json<ProductFeaturesPageLoaderResponse>({
      productPageData: fetchResponse.value.productPageData,
      baselineFallback: fetchResponse.value.baselineFallback,
      productFallback: fetchResponse.value.productFallback,
      listSlug: slug,
      domain,
      locale,
      language,
    })
  } else {
    throw new Response('Not Found', {status: 404})
  }
}

const ProductReview = () => {
  const {productPageData, baselineFallback, productFallback, listSlug} =
    useLoaderData<ProductFeaturesPageLoaderResponse>()
  const {
    product,
    factorsToConsider,
    productShortName: shortName,
    factorsSummary,
    commonQuestions,
    userValue,
    conclusion,
    variantImage,
  } = productPageData || {}
  const {language, locale, currencyCode, hostname, domain, renderTimestamp} = useRouteLoaderData(
    'root',
  ) as RootLoaderResponse
  const {siteName, styles: stylesBydomain} = getDomainConfig(domain, locale)
  const {
    dataResponse: {products: listSlugProducts} = {},
    displayCookieConsent,
    showCookieConsentOnBottom,
    clickId,
  } = useOutletContext<RootAndListContext>()

  const translations = getTranslation(language)

  const productIndex = listSlugProducts?.findIndex((p) => p.asin === product.asin)

  const {name, chosenBy, listScore} = product || {}

  const productShortName = shortName ?? name

  const processedScore = productFallback ? 9 + 0.1 * getAsinBasedSemiRandomNumber(product.asin, 0, 7) : (listScore ?? 9)

  const badge = productIndex === 0 ? 'winner' : undefined

  const formattedFactorsSummary = factorsSummary.replaceAll('<b>', '').replaceAll('</b>', '')

  const affiliateLinkWithClickId = product.affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const reportWidgetClick = useReportWidgetClick()

  const scrollToTop = useCallback(() => {
    reportWidgetClick({
      widget_name: 'scroll-to-top',
      widget_place: 'top',
    })

    window.scrollTo({top: 0, behavior: 'smooth'})
  }, [reportWidgetClick])

  const [productPageRef] = useReportWidgetSeen({
    widget_name: 'product-page',
    widget_place: 'top',
    variation: baselineFallback ? 'baseline-fallback' : 'regular',
  })
  const [productCardDescriptionRef] = useReportWidgetSeen({
    widget_name: 'product-card-decription-section',
    widget_place: 'top',
  })
  const [keyFeaturesRef] = useReportWidgetSeen({
    widget_name: 'key-features-section',
    widget_place: 'top',
  })
  const [factorsToConsiderRef] = useReportWidgetSeen({
    widget_name: 'factors-to-consider-section',
    widget_place: 'middle',
  })
  const [userValueRef] = useReportWidgetSeen({
    widget_name: 'user-value-section',
    widget_place: 'middle',
  })
  const [faqRef] = useReportWidgetSeen({
    widget_name: 'faq-section',
    widget_place: 'bottom',
  })
  const [bottomLineRef] = useReportWidgetSeen({
    widget_name: 'bottom-line-section',
    widget_place: 'bottom',
  })

  const reportClickout = useSendPartnerProductClickoutCustom()

  const userValueSectionRenderData = [
    {
      valueFieldName: 'valueForMoney',
      ratingValueFieldName: 'valueForMoneyStars',
      label: translations.ProductFeaturesPage.UserValue.valueForMoney,
      icon: <ValueForMoneyIcon className="shrink-0 tablet:size-6" />,
    },
    {
      valueFieldName: 'performanceAndFeatures',
      ratingValueFieldName: 'performanceAndFeaturesStars',
      label: translations.ProductFeaturesPage.UserValue.performanceAndFeatures,
      icon: <QualityIcon className="shrink-0 tablet:size-6" />,
    },
    {
      valueFieldName: 'userExperience',
      ratingValueFieldName: 'userExperienceStars',
      label: translations.ProductFeaturesPage.UserValue.userExperience,
      icon: <EasyToUseIcon className="shrink-0 tablet:size-6" />,
    },
  ] as {
    valueFieldName: keyof UserValueProps
    ratingValueFieldName: keyof UserValueStarsProps
    label: string
    icon: React.JSX.Element
  }[]

  const scoreFormatter = new Intl.NumberFormat(language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
  const productScore = scoreFormatter.format(processedScore)

  const handleClick = (
    e: SyntheticEvent,
    section:
      | 'product-card-decription'
      | 'key-features'
      | 'factors-to-consider'
      | 'user-value'
      | 'faq'
      | 'page-bottom'
      | 'other-discounted-product',
    element: 'image' | 'cta',
  ) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(affiliateLinkWithClickId, '_blank')

    reportClickout({
      index: productIndex ?? 10,
      item_list_id: listSlug,
      click_element_name: `${section}-${element}`,
      partner_item_brand: product.brand || '',
      partner_item_id: product?.asin || '',
      item_name: productShortName || '',
      item_brand: product?.brand || '',
      click_id: product?.affiliateLink?.clickId,
      affiliation: product?.affiliateLink?.type,
      is_dws: product?.isSellerProduct,
      location_id: locale,
      traffic: 'unknown',
      gclid: undefined,
      sys_id: undefined,
      target: affiliateLinkWithClickId,
    })
    reportWidgetClick({
      widget_name: element,
      widget_place: section,
    })
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: productShortName,
            image: [product.image],
            description: productShortName,
            brand: {
              '@type': 'Brand',
              name: product.brand,
            },
            offers: {
              '@type': 'Offer',
              url: `https://${hostname}${affiliateLinkWithClickId}`,
              priceCurrency: currencyCode,
              price: product.price?.amount,
              priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
              itemCondition: 'https://schema.org/NewCondition',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: product.brand ?? 'Amazon',
              },
            },
          }),
        }}
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: `${+productScore}`,
              bestRating: 10,
              worstRating: 0,
            },
            author: {
              '@type': 'Organization',
              name: siteName,
            },
            reviewBody: `${conclusion}`,
            itemReviewed: {
              '@type': 'Product',
              name: `${productShortName}`,
              review: {
                '@context': 'https://schema.org/',
                '@type': 'Review',
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: `${+productScore}`,
                  bestRating: 10,
                  worstRating: 0,
                },
                author: {
                  '@type': 'Organization',
                  name: siteName,
                },
                reviewBody: `${conclusion}`,
              },
            },
          }),
        }}
      ></script>

      <div
        className={clsx(
          'content-container-product-page mx-auto',
          !displayCookieConsent || showCookieConsentOnBottom ? 'mt-9 mobile:mt-5' : 'mt-5 mobile:mt-10',
        )}
        ref={productPageRef}
      >
        <div className="flex flex-col gap-4 tablet:gap-2">
          <Link
            reloadDocument
            to={`/${listSlug}`}
            className={clsx('flex flex-row items-center justify-start gap-2 tablet:text-xs')}
            aria-label="back to list"
            onClick={() => {
              reportWidgetClick({
                widget_name: 'back-to-list',
                widget_place: 'top',
              })
            }}
          >
            <BackArrowIcon className="size-4" />
            {translations.ProductFeaturesPage.backToList}
          </Link>

          {/* -------------------PLACEMENT--------------------- */}
          {!productFallback && productIndex !== undefined && (
            <ProductCardBadge
              className={clsx(
                'z-40 mt-5 flex h-full w-fit items-center justify-center overflow-hidden rounded-lg mobile:mt-3',
                '[&>div>p]:px-3 [&>div>span]:text-xl [&>div>span]:font-semibold',
              )}
              index={productIndex}
              badge={badge}
              coupon={product.coupon}
            />
          )}

          {/* -------------------PRODUCT NAME--------------------- */}
          <h1 className="line-clamp-2 text-[20px] font-semibold capitalize text-[#363636] tablet:text-base">
            {productShortName}
          </h1>

          {/* -------------------PRODUCT CARD--------------------- */}
          <div
            ref={productCardDescriptionRef}
            className={clsx(
              'border-gray relative flex h-full min-h-[227px] w-full flex-row rounded-lg border bg-white shadow-product-page-section',
              'tablet:min-h-[170px] tablet:flex-row-reverse tablet:justify-evenly tablet:gap-4 tablet:pl-5 tablet:pr-16 mobile:justify-evenly',
            )}
          >
            {product?.discount ? (
              <DiscountBadge
                discount={product.discount}
                className={clsx(
                  'absolute left-0 top-5 z-10 rounded-r-xl bg-blue-50 px-8 py-1',
                  '[&>p]:!text-base [&>p]:!lowercase [&>p]:!text-blue-600 [&>p]:tablet:!text-sm [&>p]:tablet:!font-light [&>p]:tablet:!text-white',
                  'tablet:left-auto tablet:right-0 tablet:top-3 tablet:rounded-l-2xl tablet:rounded-r-none tablet:bg-blue-500 tablet:px-3 tablet:py-0.5',
                )}
              />
            ) : null}

            <div className="relative flex w-full flex-col items-center justify-center tablet:w-fit tablet:gap-1">
              <a
                href={affiliateLinkWithClickId}
                onClick={(e) => handleClick(e, 'product-card-decription', 'image')}
                target="_blank"
                rel="sponsored noreferrer"
                aria-label={`view ${productShortName}`}
                className="absolute inset-0"
              ></a>
              <img src={product.image} alt={productShortName} className="size-44 object-scale-down tablet:size-24" />
              <span className="text-[20px] font-bold text-black tablet:text-base mobile:-mb-2">{product.brand}</span>
            </div>
            <div
              className={clsx(
                'ml-auto flex w-56 min-w-56 flex-col items-center justify-center border-l border-l-gray-300',
                'tablet:ml-0 tablet:w-fit tablet:min-w-fit tablet:border-none',
              )}
            >
              <IconAmazonLogoSvg className="mb-1 mt-1 size-16 object-scale-down tablet:hidden" />
              <div className="flex flex-row items-end gap-2">
                <span
                  className={clsx(
                    'flex justify-center text-4xl leading-9',
                    'tablet:text-3xl tablet:font-light tablet:leading-[30px]',
                    stylesBydomain.LISTPAGE_BADGE_SCORE_TEXT,
                    'listpage_badge_score_text',
                  )}
                >
                  {productScore}
                </span>
                <span className="flex items-center text-[20px] font-light text-[#4F4F4F] tablet:text-sm">
                  {translations.ProductFeaturesPage.scoreSuffix}
                </span>
              </div>
            </div>
          </div>

          <div className="my-4 border-t-gray-300 desktop:hidden tablet:border-t" />

          {/* -------------------KEY FEATURES--------------- */}

          {product?.keyFeatures && !!product.keyFeatures.length ? (
            <div className={clsx('flex flex-row desktop:my-4 tablet:flex-col tablet:px-4')} ref={keyFeaturesRef}>
              <div className="pr-10">
                <div className="mb-1 flex flex-row items-center gap-3">
                  <KeyFeaturesIcon className="size-[18px] tablet:size-4" />
                  <h2 className={clsx('text-lg font-semibold tablet:text-base')}>
                    {translations.ProductFeaturesPage.KeyFeatures.title}
                  </h2>
                </div>
                <div>
                  <ul className="list-outside" data-testid="key-feature-content">
                    {product?.keyFeatures?.slice(0, 3)?.map(({feature, description}, index) => (
                      <li
                        key={`${feature}-${index}`}
                        className="mb-1 flex flex-row items-start text-lg tablet:text-base"
                      >
                        <p className="mb-auto font-medium">{index + 1}.</p>
                        <p className="ml-1 font-light [&>b]:font-semibold">
                          <b>{feature}</b>: {description}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div
                className={clsx(
                  'ml-auto mt-3 flex w-56 min-w-56 flex-col items-center justify-center gap-5',
                  'tablet:ml-0 tablet:w-full tablet:min-w-fit tablet:flex-row tablet:justify-between',
                )}
              >
                <IconAmazonLogoSvg className="mb-1 mt-1 h-6 w-20 object-scale-down tablet:mb-auto" />
                <div className="flex flex-col gap-5 tablet:gap-4">
                  <a
                    href={affiliateLinkWithClickId}
                    onClick={(e) => handleClick(e, 'key-features', 'cta')}
                    aria-label={`view ${productShortName}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    className={clsx(
                      'mx-auto flex h-[50px] w-full max-w-[275px] cursor-pointer items-center justify-center rounded-3xl px-8 text-center text-lg uppercase text-white',
                      'tablet:h-10 tablet:max-w-fit tablet:text-base mobile:px-4',
                      stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
                      'listpage_button_primary',
                    )}
                  >
                    {translations.ProductFeaturesPage.ctaText}
                  </a>
                  {chosenBy && chosenBy > 0 ? (
                    <div className="flex flex-row items-center gap-2 text-base tablet:gap-1 tablet:text-sm">
                      <ChosenByCheckIcon />
                      <p className="font-light text-[#64748B]">{translations.Badge.chosenBy(chosenBy)}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          {/* ----------------FACTORS TO CONSIDER---------------- */}
          {factorsToConsider && !!factorsToConsider.length ? (
            <div
              className="my-3 flex flex-col gap-4 rounded-lg bg-gray-50 px-8 pb-6 pt-4 shadow-product-page-section tablet:px-4"
              ref={factorsToConsiderRef}
            >
              <h2 className="text-[28px] font-medium tablet:text-2xl">
                {translations.ProductFeaturesPage.FactorsToConsider.title}
              </h2>
              <ul className="flex flex-col gap-2">
                {factorsToConsider?.map((factor, index) => (
                  <Fragment key={`${factor}-${index}`}>
                    <li className="flex flex-row items-start justify-start gap-5 text-lg tablet:text-base">
                      <CheckBWIcon className="mt-1.5 flex-shrink-0" />
                      <p className="font-light text-carbon-900">{factor}</p>
                    </li>
                  </Fragment>
                ))}
              </ul>
              <div
                className={clsx(
                  'relative flex min-h-[204px] items-center justify-center rounded-lg bg-white shadow-product-page-section tablet:min-h-[140px]',
                )}
              >
                <a
                  href={affiliateLinkWithClickId}
                  onClick={(e) => handleClick(e, 'factors-to-consider', 'image')}
                  aria-label={`view ${productShortName}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                  className="absolute inset-0"
                ></a>
                <img
                  alt="example product"
                  src={variantImage ?? product.image}
                  className="size-36 object-scale-down tablet:size-28"
                />
              </div>
              {factorsSummary ? (
                <span className="text-center text-lg font-semibold tablet:px-2 tablet:text-base">
                  {formattedFactorsSummary}
                </span>
              ) : null}
              {/*  ---------------------- IMPROVING USER VALUE with STARS RATING ------------------------- */}

              <a
                href={affiliateLinkWithClickId}
                onClick={(e) => handleClick(e, 'factors-to-consider', 'cta')}
                aria-label={`view ${productShortName}`}
                target="_blank"
                rel="sponsored noreferrer"
                className={clsx(
                  'mx-auto flex h-[50px] w-full max-w-[275px] cursor-pointer items-center justify-center rounded-3xl px-8 text-center text-lg uppercase text-white',
                  'tablet:h-10 tablet:max-w-fit tablet:text-base',
                  stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
                  'listpage_button_primary',
                )}
              >
                {translations.ProductFeaturesPage.ctaText}
              </a>
            </div>
          ) : null}

          {/* ---------------USER VALUE------------------ */}
          {userValue ? (
            <div
              className="my-3 flex flex-col gap-5 rounded-lg bg-gray-50 px-8 py-4 shadow-product-page-section tablet:px-4"
              ref={userValueRef}
            >
              {userValueSectionRenderData?.map(({label, icon, valueFieldName}, index) => {
                return (
                  <Fragment key={`${label}-${index}`}>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row items-center gap-3 text-2xl font-medium tablet:text-[20px]">
                        {icon}
                        {label}
                      </div>
                      <p className="text-lg font-light text-carbon-900 tablet:text-base">{userValue[valueFieldName]}</p>
                    </div>
                  </Fragment>
                )
              })}
              <a
                href={affiliateLinkWithClickId}
                onClick={(e) => handleClick(e, 'user-value', 'cta')}
                aria-label={`view ${productShortName}`}
                target="_blank"
                rel="sponsored noreferrer"
                className={clsx(
                  'mx-auto flex h-[50px] w-full max-w-[275px] cursor-pointer items-center justify-center rounded-3xl px-8 text-center text-lg uppercase text-white',
                  'tablet:h-10 tablet:max-w-fit tablet:text-base',
                  stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
                  'listpage_button_primary',
                )}
              >
                {translations.ProductFeaturesPage.ctaText}
              </a>
            </div>
          ) : null}

          {/* ------------------FAQ------------------- */}
          {commonQuestions && !!commonQuestions.length ? (
            <div
              className={clsx(
                'my-3 flex flex-col gap-4 rounded-lg bg-blue-50 px-8 py-4 shadow-product-page-section tablet:gap-2 tablet:px-4',
              )}
              ref={faqRef}
            >
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: commonQuestions.map(({question, answer}) => ({
                      '@type': 'Question',
                      name: question,
                      acceptedAnswer: {
                        '@type': 'Answer',
                        text: `<p>${answer}</p>`,
                      },
                    })),
                  }),
                }}
              />
              <h2 className="text-2xl font-semibold">{translations.ProductFeaturesPage.Faq.title}</h2>
              <ul className="flex flex-col gap-3">
                {commonQuestions?.map((faq, index) => (
                  <li key={`faq-${index}`} className="flex flex-col divide-y divide-blue-400">
                    <h3 className="py-3 text-[20px] font-normal leading-7 text-carbon-900 tablet:text-base">
                      {faq.question}
                    </h3>
                    <p className="py-2 text-[18px] font-extralight text-carbon-700 tablet:text-sm">{faq.answer}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* -----------------BOTTOM LINE------------------ */}
          {conclusion ? (
            <div
              className="my-3 flex flex-col gap-3 rounded-lg border border-blue-300 bg-white px-3 py-4 shadow-product-page-section"
              ref={bottomLineRef}
            >
              <h2 className="text-2xl font-medium text-black">{translations.ProductFeaturesPage.BottmoLine.title}</h2>
              <p className="text-base font-light text-carbon-900">{conclusion}</p>
            </div>
          ) : null}

          <a
            href={affiliateLinkWithClickId}
            onClick={(e) => handleClick(e, 'page-bottom', 'cta')}
            aria-label={`view ${productShortName}`}
            target="_blank"
            rel="sponsored noreferrer"
            className={clsx(
              'mx-auto mt-3 flex h-[50px] w-full max-w-[275px] cursor-pointer items-center justify-center rounded-3xl px-8 text-center text-lg uppercase text-white',
              'tablet:h-10 tablet:max-w-fit tablet:text-base',
              stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
              'listpage_button_primary',
            )}
          >
            {translations.ProductFeaturesPage.ctaText}
          </a>

          <button
            type="button"
            className="mb-7 mt-5 flex flex-col items-center justify-center gap-2 text-lg font-light tablet:gap-1 tablet:text-xs"
            aria-label="back to top"
            onClick={scrollToTop}
          >
            <BackArrowIcon className="size-7 rotate-90 tablet:size-4" />
            {translations.ProductFeaturesPage.backToTop}
          </button>
        </div>
      </div>
    </>
  )
}

export default ProductReview
